/**
 * DayView — everything that happened on a single date, for the current user.
 * Reachable from MemberJourney rows and from the FitScore weekly sheet.
 * Empty sections show "Nothing logged" — never a zero, never a spinner.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { apiClient } from "../../services/api";
import TopBar from "../../components/layout/TopBar";
import BottomNav from "../../components/layout/BottomNav";

function Section({ title, children, testid }) {
  return (
    <section data-testid={testid} style={{ background: "white", border: "1px solid #EEECE9", borderRadius: 14, padding: "14px 16px", marginBottom: 12 }}>
      <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: "#8A8884", fontWeight: 800, marginBottom: 8 }}>{title}</div>
      {children}
    </section>
  );
}

function Empty({ label }) {
  return <div style={{ fontSize: 13, color: "#8A8884", fontStyle: "italic" }}>{label || "Nothing logged"}</div>;
}

export default function DayView() {
  const { date } = useParams();
  const navigate = useNavigate();
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiClient.get("/me/day", { params: { date } })
      .then((r) => setDay(r.data))
      .catch(() => setDay(null))
      .finally(() => setLoading(false));
  }, [date]);

  const pretty = date ? new Date(date + "T12:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div className="min-h-screen" style={{ background: "#F4F3F1", paddingBottom: 96 }} data-testid="day-view">
      <TopBar />
      <main style={{ maxWidth: 640, margin: "0 auto", padding: "16px 16px 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <button onClick={() => navigate(-1)} aria-label="Back" data-testid="day-back"
            style={{ width: 36, height: 36, borderRadius: "50%", background: "white", border: "1px solid #E7E5E1", display: "grid", placeItems: "center", cursor: "pointer" }}>
            <ChevronLeft size={18} color="#0C0C0B" />
          </button>
          <div>
            <h1 data-testid="day-title" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 22, color: "#0C0C0B", margin: 0, lineHeight: 1 }}>{pretty}</h1>
            <div style={{ fontSize: 11, color: "#8A8884", marginTop: 2, letterSpacing: "0.04em", textTransform: "uppercase" }}>Day view</div>
          </div>
        </div>

        {loading && <div style={{ padding: 40, textAlign: "center", color: "#8A8884" }}>Loading…</div>}

        {!loading && day && (
          <>
            <Section title="FitScore" testid="day-fitscore">
              {day.fitscore ? (
                <>
                  <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 900, fontSize: 42, color: "#0C0C0B", lineHeight: 1 }}>{day.fitscore.total ?? "—"}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 12 }}>
                    {["workout", "nutrition", "sleep", "activity"].map((k) => (
                      <div key={k} style={{ background: "#F4F3F1", borderRadius: 8, padding: "8px 6px", textAlign: "center" }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: "#0C0C0B" }}>{day.fitscore.components?.[k] ?? "—"}</div>
                        <div style={{ fontSize: 10, color: "#8A8884", textTransform: "capitalize" }}>{k}</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : <Empty />}
            </Section>

            <Section title="Workouts" testid="day-workouts">
              {day.workouts?.length ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {day.workouts.map((w) => (
                    <div key={w.session_id} data-testid={`day-workout-${w.session_id}`} style={{ display: "flex", justifyContent: "space-between", padding: "10px 12px", background: "#F4F3F1", borderRadius: 10, fontSize: 13 }}>
                      <span>{w.name || w.module || "Workout"} · {w.exercises?.length || 0} ex</span>
                      <span style={{ color: "#8A8884" }}>{w.duration_minutes || "?"} min</span>
                    </div>
                  ))}
                </div>
              ) : <Empty />}
            </Section>

            <Section title="Nutrition" testid="day-nutrition">
              {day.nutrition ? (
                <div style={{ fontSize: 13, color: "#0C0C0B" }}>
                  <strong>{day.nutrition.calories} kcal</strong> · {Math.round(day.nutrition.protein_g)}g P · {Math.round(day.nutrition.carbs_g)}g C · {Math.round(day.nutrition.fat_g)}g F · {day.nutrition.items_count} items
                </div>
              ) : <Empty />}
            </Section>

            <Section title="Hydration" testid="day-hydration">
              {day.hydration ? (
                <div style={{ fontSize: 13 }}>{day.hydration.glasses} / {day.hydration.goal} glasses</div>
              ) : <Empty />}
            </Section>

            <Section title="Morning check-in" testid="day-morning">
              {day.morning_checkin ? (
                <div style={{ fontSize: 13 }}>Sleep {day.morning_checkin.sleep}/5 · Soreness {day.morning_checkin.soreness}/5 · Energy {day.morning_checkin.energy}/5 · Score {day.morning_checkin.score ?? "—"}</div>
              ) : <Empty />}
            </Section>

            <Section title="Arena check-in" testid="day-arena">
              {day.arena_checkin ? (
                <div style={{ fontSize: 13 }}>Challenge {day.arena_checkin.challenge_id} · Day {day.arena_checkin.day_index}{day.arena_checkin.kind ? ` · ${day.arena_checkin.kind}` : ""}</div>
              ) : <Empty />}
            </Section>

            <Section title="FitPoints earned" testid="day-fitpoints">
              <div style={{ fontSize: 13 }}>{day.fitpoints_earned > 0 ? `+${day.fitpoints_earned} FP` : "Nothing earned"}</div>
            </Section>
          </>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
